import React, { useState, useEffect } from 'react';
import { Card } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import {
  RefreshCw,
  Copy,
  Check,
  CheckCircle,
  Circle,
  FileText,
  Users,
  Presentation,
  MessageSquare,
  FileImage,
  Star
} from 'lucide-react';
import { adminAPI } from '../../services/api';
import { useToast } from '../ui/use-toast';

export default function TaskStatistics() {
  const [companies, setCompanies] = useState([]);
  const [loading, setLoading] = useState(false);
  const [copiedTask, setCopiedTask] = useState(null);
  const { toast } = useToast();

  const taskDefinitions = [
    { key: 'company_details', label: 'Intake', icon: FileText },
    { key: 'networking_passes', label: 'Passes', icon: Users },
    { key: 'pitch_submission', label: 'Pitch', icon: Presentation },
    { key: 'brand_promotion', label: 'Brand Promotion', icon: MessageSquare },
    { key: 'promotional_flyer', label: 'Flyer', icon: FileImage },
    { key: 'fo_nominations', label: 'FO', icon: Star },
  ];

  useEffect(() => {
    fetchStatistics();
  }, []);

  const fetchStatistics = async () => {
    setLoading(true);
    try {
      const response = await adminAPI.getCompaniesWithProgress();
      setCompanies(response.data.companies);
    } catch (error) {
      console.error('Error fetching statistics:', error);
      toast({
        title: 'Error',
        description: 'Failed to fetch task statistics',
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  };

  const calculateTaskStats = (taskKey) => {
    const completedCompanies = companies.filter(c => c.tasks[taskKey]);
    const total = companies.length;
    const completed = completedCompanies.length;
    const percentage = total > 0 ? Math.round((completed / total) * 100) : 0;

    return {
      completed,
      total,
      percentage,
      companies: completedCompanies
    };
  };

  const handleCopyCompanies = (taskKey, taskLabel) => {
    const stats = calculateTaskStats(taskKey);

    if (stats.completed === 0) {
      toast({
        title: 'No Companies',
        description: `No companies have completed ${taskLabel} yet`,
        variant: 'default',
      });
      return;
    }

    const companyNames = stats.companies.map(c => c.companyName).join('\n');

    navigator.clipboard.writeText(companyNames).then(() => {
      setCopiedTask(taskKey);
      toast({
        title: 'Copied!',
        description: `${stats.completed} company name${stats.completed === 1 ? '' : 's'} copied to clipboard`,
      });

      // Reset the copied state after 2 seconds
      setTimeout(() => {
        setCopiedTask(null);
      }, 2000);
    }).catch(() => {
      toast({
        title: 'Error',
        description: 'Failed to copy to clipboard',
        variant: 'destructive',
      });
    });
  };

  const getProgressColor = (percentage) => {
    if (percentage === 100) return 'bg-green-600';
    if (percentage >= 60) return 'bg-blue-600';
    if (percentage >= 30) return 'bg-yellow-600';
    return 'bg-red-600';
  };

  const getProgressBadgeVariant = (percentage) => {
    if (percentage === 100) return 'default';
    if (percentage >= 60) return 'default';
    if (percentage >= 30) return 'secondary';
    return 'destructive';
  };

  return (
    <Card className="w-full h-full flex flex-col overflow-hidden">
      <div className="flex-shrink-0 bg-white px-4 py-3 border-b">
        <div className="flex flex-col lg:flex-row justify-between items-start lg:items-center gap-3">
          <div>
            <h2 className="text-xl sm:text-2xl font-bold">Task Completion Statistics</h2>
            <p className="text-sm text-gray-500 mt-1">
              Track completion rates for each task across all companies
            </p>
          </div>
          <Button
            variant="outline"
            size="sm"
            onClick={fetchStatistics}
            disabled={loading}
          >
            <RefreshCw className={`h-4 w-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
        </div>
      </div>

      <div className="flex-1 overflow-y-auto p-4 sm:p-6">
        {loading ? (
          <div className="flex justify-center py-12">
            <div className="animate-spin h-8 w-8 border-4 border-gray-300 border-t-blue-600 rounded-full"></div>
          </div>
        ) : companies.length === 0 ? (
          <div className="text-center py-12 text-gray-500">
            No company data available
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
            {taskDefinitions.map((task) => {
              const stats = calculateTaskStats(task.key);
              const Icon = task.icon;

              return (
                <Card
                  key={task.key}
                  className="p-4 hover:shadow-lg transition-shadow cursor-pointer border-2 hover:border-blue-300"
                  onClick={() => handleCopyCompanies(task.key, task.label)}
                >
                  <div className="space-y-4">
                    {/* Header */}
                    <div className="flex items-start justify-between">
                      <div className="flex items-center gap-3">
                        <div className="p-2 bg-blue-100 rounded-lg">
                          <Icon className="h-5 w-5 text-blue-600" />
                        </div>
                        <div>
                          <h3 className="font-semibold text-lg">{task.label}</h3>
                          <p className="text-sm text-gray-500">Click to copy names</p>
                        </div>
                      </div>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleCopyCompanies(task.key, task.label);
                        }}
                        className={`h-8 w-8 p-0 transition-all ${
                          copiedTask === task.key
                            ? 'bg-green-100 hover:bg-green-200'
                            : 'hover:bg-blue-100'
                        }`}
                      >
                        {copiedTask === task.key ? (
                          <Check className="h-4 w-4 text-green-600" />
                        ) : (
                          <Copy className="h-4 w-4 text-gray-700" />
                        )}
                      </Button>
                    </div>

                    {/* Stats */}
                    <div className="space-y-2">
                      <div className="flex items-center justify-between">
                        <span className="text-2xl font-bold text-gray-900">
                          {stats.completed} / {stats.total}
                        </span>
                        <Badge variant={getProgressBadgeVariant(stats.percentage)}>
                          {stats.percentage}%
                        </Badge>
                      </div>

                      {/* Progress Bar */}
                      <div className="w-full bg-gray-200 rounded-full h-2">
                        <div
                          className={`h-2 rounded-full transition-all ${getProgressColor(stats.percentage)}`}
                          style={{ width: `${stats.percentage}%` }}
                        />
                      </div>

                      {/* Companies Text */}
                      <div className="flex items-center justify-between text-sm text-gray-600">
                        <span>{stats.completed} completed</span>
                        <span>{stats.total - stats.completed} pending</span>
                      </div>
                    </div>

                    {/* Icon indicator */}
                    <div className="flex items-center gap-2 text-xs text-gray-500 pt-2 border-t">
                      {stats.completed > 0 ? (
                        <>
                          <CheckCircle className="h-3.5 w-3.5 text-green-500" />
                          <span>Companies have submitted</span>
                        </>
                      ) : (
                        <>
                          <Circle className="h-3.5 w-3.5 text-gray-300" />
                          <span>No submissions yet</span>
                        </>
                      )}
                    </div>
                  </div>
                </Card>
              );
            })}
          </div>
        )}
      </div>

      {/* Footer Summary */}
      {companies.length > 0 && (
        <div className="flex-shrink-0 px-4 py-3 bg-gray-50 border-t">
          <div className="flex flex-col sm:flex-row justify-between items-center gap-2">
            <p className="text-sm text-gray-700">
              Total: <span className="font-medium">{companies.length}</span> companies registered
            </p>
            <div className="flex gap-4 text-sm">
              <div className="flex items-center gap-1.5">
                <CheckCircle className="h-4 w-4 text-green-500" />
                <span>Completed</span>
              </div>
              <div className="flex items-center gap-1.5">
                <Circle className="h-4 w-4 text-gray-300" />
                <span>Pending</span>
              </div>
            </div>
          </div>
        </div>
      )}
    </Card>
  );
}
